'use client';

import { c as _c } from "react/compiler-runtime";
import { BedSolid, StayPictogram } from '@byway/icons';
import { useTranslations } from 'next-intl';
import { formatStationDistanceMeters } from '~/components/TripOverview/accommodationPage/formatStationDistance';
import { sanitizeText } from '~/utils/sanitizeText';
import styles from './AccommodationCard.module.css';
import type { AccommodationCardProps } from './AccommodationCard.types';
import { AccommodationCardNightStepper } from './AccommodationCardNightStepper';
import { AccommodationCardSleeperContent } from './AccommodationCardSleeperContent';
import { AccommodationCardStayContent } from './AccommodationCardStayContent';
import { ItineraryCard } from './ItineraryCard';
import type { TagItem } from './ItineraryCard.types';
function getBodyText(body: AccommodationCardProps['body']): string {
  const textCard = body.find(card => card.type === 'text');
  return textCard && 'text' in textCard ? sanitizeText(String(textCard.text)) : '';
}
function getAccommodationDetails(body: AccommodationCardProps['body']) {
  const accCard = body.find(card => card.type === 'accommodation');
  if (!accCard || !('defaultAccommodation' in accCard) || !accCard.defaultAccommodation) return null;
  return accCard.defaultAccommodation as {
    accommodation: {
      title: string;
      roomName: string;
      boardBasis?: string;
    };
  };
}
export const AccommodationCard = t0 => {
  const $ = _c(73);
  const {
    title,
    nights,
    images,
    body,
    section,
    stayItemId,
    tripId,
    accommodationStay,
    accommodationVariant: t1,
    sleeperDetails
  } = t0;
  const accommodationVariant = t1 === undefined ? "stay-expanded" : t1;
  const t = useTranslations("journey");
  let t2;
  if ($[0] !== body) {
    t2 = getBodyText(body);
    $[0] = body;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const bodyText = t2;
  let t3;
  if ($[2] !== body) {
    t3 = getAccommodationDetails(body);
    $[2] = body;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const accommodationDetails = t3;
  const accommodationTitle = accommodationStay?.accommodation.title ?? accommodationDetails?.accommodation.title;
  let t4;
  if ($[4] !== accommodationStay?.accommodation.selectedRooms) {
    t4 = accommodationStay?.accommodation.selectedRooms ?? [];
    $[4] = accommodationStay?.accommodation.selectedRooms;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const selectedRooms = t4;
  const fallbackRoomName = accommodationStay?.accommodation.roomName ?? accommodationDetails?.accommodation.roomName;
  let t5;
  if ($[6] !== fallbackRoomName) {
    t5 = fallbackRoomName ? [{
      id: "fallback-room",
      displayName: fallbackRoomName
    }] : [];
    $[6] = fallbackRoomName;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const fallbackRooms = t5;
  let t6;
  if ($[8] !== fallbackRooms || $[9] !== selectedRooms) {
    t6 = selectedRooms.length > 0 ? selectedRooms.map(_temp) : fallbackRooms;
    $[8] = fallbackRooms;
    $[9] = selectedRooms;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const rooms = t6;
  let t7;
  if ($[11] !== t) {
    t7 = t("overview_accommodation_included");
    $[11] = t;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <BedSolid size="small" decorative={true} />;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t7) {
    t9 = [{
      label: t7,
      variant: "positive",
      icon: t8
    }];
    $[14] = t7;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const overviewTags = t9;
  const arrivalMeters = accommodationStay?.accommodation.distanceFromArrivalStationMeters;
  const departureMeters = accommodationStay?.accommodation.distanceFromDepartureStationMeters;
  let t10;
  if ($[16] !== arrivalMeters || $[17] !== t) {
    t10 = arrivalMeters != null && arrivalMeters > 0 ? t("accommodation_distance_arrival", {
      distance: formatStationDistanceMeters(arrivalMeters)
    }) : null;
    $[16] = arrivalMeters;
    $[17] = t;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const arrivalDistance = t10;
  let t11;
  if ($[19] !== departureMeters || $[20] !== t) {
    t11 = departureMeters != null && departureMeters > 0 ? t("accommodation_distance_departure", {
      distance: formatStationDistanceMeters(departureMeters)
    }) : null;
    $[19] = departureMeters;
    $[20] = t;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const departureDistance = t11;
  const showBreakfast = accommodationStay?.accommodation.breakfastIncluded || accommodationDetails?.accommodation.boardBasis === "BB";
  let t12;
  if ($[22] !== accommodationStay?.accommodation.attributes) {
    t12 = accommodationStay?.accommodation.attributes ?? [];
    $[22] = accommodationStay?.accommodation.attributes;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const attributes = t12;
  if (section === "accommodation") {
    if (accommodationVariant === "sleeper" && sleeperDetails) {
      let t13;
      if ($[24] !== images || $[25] !== sleeperDetails) {
        t13 = <AccommodationCardSleeperContent images={images} sleeperDetails={sleeperDetails} />;
        $[24] = images;
        $[25] = sleeperDetails;
        $[26] = t13;
      } else {
        t13 = $[26];
      }
      return t13;
    }
    let t13;
    if ($[27] !== accommodationDetails || $[28] !== accommodationStay || $[29] !== accommodationTitle || $[30] !== accommodationVariant || $[31] !== arrivalDistance || $[32] !== attributes || $[33] !== bodyText || $[34] !== departureDistance || $[35] !== images || $[36] !== rooms || $[37] !== showBreakfast || $[38] !== stayItemId || $[39] !== title || $[40] !== tripId) {
      t13 = <AccommodationCardStayContent placeTitle={title} images={images} variant={accommodationVariant} accommodationTitle={accommodationTitle} rooms={rooms} accommodationDetails={accommodationDetails} bodyText={bodyText} accommodationStay={accommodationStay} arrivalDistance={arrivalDistance} departureDistance={departureDistance} showBreakfast={showBreakfast} attributes={attributes} tripId={tripId} itemId={stayItemId} />;
      $[27] = accommodationDetails;
      $[28] = accommodationStay;
      $[29] = accommodationTitle;
      $[30] = accommodationVariant;
      $[31] = arrivalDistance;
      $[32] = attributes;
      $[33] = bodyText;
      $[34] = departureDistance;
      $[35] = images;
      $[36] = rooms;
      $[37] = showBreakfast;
      $[38] = stayItemId;
      $[39] = title;
      $[40] = tripId;
      $[41] = t13;
    } else {
      t13 = $[41];
    }
    return t13;
  }
  let t13;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <StayPictogram size="small" decorative={true} />;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  let t14;
  if ($[43] !== t || $[44] !== title) {
    t14 = t("itinerary_stay_in", {
      place: title
    });
    $[43] = t;
    $[44] = title;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  let t15;
  if ($[46] !== t14) {
    t15 = <ItineraryCard.Header icon={t13} title={t14} />;
    $[46] = t14;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  let t16;
  if ($[48] !== images) {
    t16 = images.length > 0 ? <ItineraryCard.Gallery images={images} /> : null;
    $[48] = images;
    $[49] = t16;
  } else {
    t16 = $[49];
  }
  let t17;
  if ($[50] !== nights || $[51] !== stayItemId || $[52] !== tripId) {
    t17 = <AccommodationCardNightStepper nights={nights} tripId={tripId} stayItemId={stayItemId} />;
    $[50] = nights;
    $[51] = stayItemId;
    $[52] = tripId;
    $[53] = t17;
  } else {
    t17 = $[53];
  }
  let t18;
  if ($[54] !== overviewTags) {
    t18 = <ItineraryCard.Tags tags={overviewTags} />;
    $[54] = overviewTags;
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  let t19;
  if ($[56] !== t) {
    t19 = t("overview_choose_accommodation");
    $[56] = t;
    $[57] = t19;
  } else {
    t19 = $[57];
  }
  let t20;
  if ($[58] !== t19) {
    t20 = <ItineraryCard.InfoBanner>{t19}</ItineraryCard.InfoBanner>;
    $[58] = t19;
    $[59] = t20;
  } else {
    t20 = $[59];
  }
  let t21;
  if ($[60] !== t18 || $[61] !== t20) {
    t21 = <div className={styles.instructionalGroup}>{t18}{t20}</div>;
    $[60] = t18;
    $[61] = t20;
    $[62] = t21;
  } else {
    t21 = $[62];
  }
  let t22;
  if ($[63] !== t17 || $[64] !== t21) {
    t22 = <div className={styles.overviewBlock}>{t17}{t21}</div>;
    $[63] = t17;
    $[64] = t21;
    $[65] = t22;
  } else {
    t22 = $[65];
  }
  let t23;
  if ($[66] !== bodyText) {
    t23 = bodyText ? <ItineraryCard.Body text={bodyText} /> : null;
    $[66] = bodyText;
    $[67] = t23;
  } else {
    t23 = $[67];
  }
  let t24;
  if ($[68] !== t15 || $[69] !== t16 || $[70] !== t22 || $[71] !== t23) {
    t24 = <ItineraryCard>{t15}{t16}{t22}{t23}</ItineraryCard>;
    $[68] = t15;
    $[69] = t16;
    $[70] = t22;
    $[71] = t23;
    $[72] = t24;
  } else {
    t24 = $[72];
  }
  return t24;
};
function _temp(room) {
  return {
    id: String(room.id),
    displayName: room.displayName
  };
}